import { type Vector2 } from '@canvas/vector-types';

import { type AnyElement, type AnyElementData } from '../all-element-types';
import {
  type ElementData,
  type Element,
  elementType,
  type ElementType,
} from '../element';

/**
 * Supported file extensions for adding images.
 */
export const supportedImageExtensions = [
  '.png',
  '.jpeg',
  '.webp',
  '.gif',
  '.jpg',
  '.svg',
];

/**
 * Supported file extensions for importing files.
 */
export const supportedFileImportExtensions = ['.pdf'];

export enum FileStatus {
  Pending = 0,
  Uploading = 1,
  Downloading = 2,
  UploadError = 3,
  DownloadError = 4,
  Ready = 5,
  LocalOnly = 6,
}

export interface ImageElementData extends ElementData<ElementType> {
  /** The file ID returned by the media service */
  fileId?: string;
  /** File data */
  blob?: Blob;
  /** URL to file */
  url?: string;

  /**
   * The size of the underlying image data, in physical pixels.
   */
  nativeSize?: Vector2 | null;
  /**
   * The mimetype of the underlying image.
   */
  mimeType?: string;
  /**
   * A SHA-512 hash of the image data, to allow us to detect images with identical contents.
   */
  imageHash?: string;
  /**
   * A boolean indicating if the image contains any transparent pixels.
   */
  isTransparent?: boolean;
  /**
   * Marks the image as not being included as an individual undo step.
   * This is useful for batched inserts such as templates.
   */
  excludeFromUndoStack?: boolean;
  rotation?: number;

  /**
   * ⚠️ If we ever add basisSize and basisPosition here, ensure that the corresponding type-guards
   * are updated accordingly in @link packages/sizing-feature-flags/src/sizing-feature-flags.ts
   */
}

export interface ImageElement extends ImageElementData, Element<ElementType> {}

export function isImage(element: AnyElement | undefined): element is ImageElement {
  return !!element && element.type === imageElementType;
}

export function isImageData(
  element: AnyElementData | undefined,
): element is ImageElementData {
  return !!element && element.type === imageElementType;
}
export const imageElementType = elementType('image');
